/**
 * Pure helpers for FormatSelect cascade (container → quantity → unit).
 * Kept separate from Vue $utils so they can be unit-tested.
 */

export function getFormatContainerLabel(format) {
    if (!format) {
        return '';
    }
    if (format.container_label) {
        return String(format.container_label).trim();
    }
    const singular = format.label_singular;
    if (typeof singular === 'string' && singular.trim()) {
        return singular.trim();
    }
    if (singular && typeof singular === 'object') {
        const localized = singular.fr || singular.en || '';
        if (localized) {
            return String(localized).trim();
        }
    }
    const label = String(format.name || format.label || '').trim();
    if (!label) {
        return '';
    }
    const withoutCapacity = label
        .replace(/\s+\d+(?:[.,]\d+)?(?:\s*(?:ml|cl|l|kg|g))?\s*$/i, '')
        .trim();
    return withoutCapacity || label;
}

export function getFormatContainerKey(format) {
    return getFormatContainerLabel(format).toLowerCase();
}

export function formatsForContainer(formats, containerKey) {
    const key = String(containerKey || '').toLowerCase();
    return (formats || []).filter(
        (format) => getFormatContainerKey(format) === key,
    );
}

export function unitsForContainer(formats, containerKey) {
    const units = [];
    const seen = new Set();
    formatsForContainer(formats, containerKey).forEach((format) => {
        const unit = format?.content_unit;
        if (!unit || seen.has(unit)) {
            return;
        }
        seen.add(unit);
        units.push(unit);
    });
    return units;
}

export function quantitiesForContainerAndUnit(formats, containerKey, unit) {
    const quantities = [];
    const seen = new Set();
    formatsForContainer(formats, containerKey)
        .filter((format) => String(format.content_unit) === String(unit))
        .forEach((format) => {
            const qtyKey = String(format.content_quantity);
            if (seen.has(qtyKey)) {
                return;
            }
            seen.add(qtyKey);
            quantities.push(format.content_quantity);
        });
    return quantities.sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (Number.isFinite(na) && Number.isFinite(nb)) {
            return na - nb;
        }
        return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
}

export function resolveFormat(formats, containerKey, quantity, unit) {
    const qtyKey = String(quantity);
    return (
        formatsForContainer(formats, containerKey).find(
            (format) =>
                String(format.content_unit) === String(unit)
                && String(format.content_quantity) === qtyKey,
        ) || null
    );
}

export const WEIGHT_UNITS = ["kg", "g"];
export const VOLUME_UNITS = ["L", "cl", "ml"];

/**
 * Classify a plain unit token for QuantityUnitSelect / FormatSelect kind-first.
 * @param {string} unit
 * @returns {"weight" | "volume" | "piece"}
 */
export function kindForPlainUnit(unit) {
    if (WEIGHT_UNITS.includes(unit)) {
        return "weight";
    }
    if (VOLUME_UNITS.includes(unit)) {
        return "volume";
    }
    return "piece";
}

/**
 * @param {{ text?: string, value?: string }[]} actions
 * @returns {{ piece: object[], weight: object[], volume: object[] }}
 */
export function groupPlainUnitsByKind(actions = []) {
    const groups = { piece: [], weight: [], volume: [] };
    (actions || []).forEach((action) => {
        const kind = kindForPlainUnit(action?.value);
        groups[kind].push(action);
    });
    return groups;
}

export function compactFormatSearchText(text) {
    return String(text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s.,]/g, '');
}

export function formatLabelMatchesSearch(label, query) {
    const rawQuery = String(query || '').trim();
    if (!rawQuery) {
        return true;
    }
    const labelLower = String(label || '').toLowerCase();
    const queryLower = rawQuery.toLowerCase();
    if (labelLower.includes(queryLower)) {
        return true;
    }
    const compactLabel = compactFormatSearchText(label);
    const compactQuery = compactFormatSearchText(rawQuery);
    return Boolean(compactQuery) && compactLabel.includes(compactQuery);
}

export function buildFormatContainerOptions(formats = []) {
    const byKey = new Map();
    (formats || []).forEach((format) => {
        const key = getFormatContainerKey(format);
        const label = getFormatContainerLabel(format);
        if (!key || byKey.has(key)) {
            return;
        }
        byKey.set(key, { key, label });
    });
    return [...byKey.values()].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
}
